import { useEffect, useMemo, useState } from 'react';
import { formatLedgerTime, shortAddress } from '../lib/format.js';

const PAGE_SIZE = 80;

function csvCell(value) {
  const text = String(value ?? '');
  const safe = /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

function sourceTime(value) {
  if (!value) return 'Source time unavailable';
  const normalized = /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function coverageLabel(program) {
  if (program.catalog.coveredBy) return 'Curated match';
  if (program.catalog.exactIdentityCount) return 'Live pair tracked';
  return 'Account reference';
}

export function CatalogTable({ programs, flows = [], meta, status, error, errorCode, retry, onSelect }) {
  const [query, setQuery] = useState('');
  const [distribution, setDistribution] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const platforms = useMemo(() => [...new Set(programs.map((program) => program.catalog.platform))].sort(), [programs]);
  const activityByProgram = useMemo(() => {
    const entries = new Map();
    flows.forEach((flow) => {
      const current = entries.get(flow.programId);
      if (!current || Number(flow.date || 0) > Number(current.date || 0)) entries.set(flow.programId, flow);
    });
    return entries;
  }, [flows]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return programs.filter((program) => {
      if (platform !== 'all' && program.catalog.platform !== platform) return false;
      const types = program.catalog.distributionTypes || [];
      if (distribution === 'distributed' && !types.includes('Onchain Distributed')) return false;
      if (distribution === 'represented' && !types.includes('Onchain Represented')) return false;
      if (!needle) return true;
      return [
        program.name,
        program.ticker,
        program.catalog.platform,
        program.catalog.assetClass,
        program.catalog.jurisdiction,
        program.catalog.issuerName,
        ...(program.catalog.identities || []).flatMap((identity) => [identity.address, identity.issuer, identity.currency]),
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [distribution, platform, programs, query]);

  useEffect(() => setVisibleCount(PAGE_SIZE), [distribution, platform, query]);

  const exportCsv = () => {
    const headers = ['Asset', 'Ticker', 'Platform', 'Asset class', 'Distribution', 'Jurisdiction', 'Issuer', 'Currency', 'Address', 'Evidence', 'Latest matched transaction', 'Transaction URL'];
    const rows = filtered.flatMap((program) => {
      const identities = program.catalog.identities?.length ? program.catalog.identities : [{}];
      const activity = activityByProgram.get(program.id) || activityByProgram.get(program.catalog.coveredBy);
      return identities.map((identity) => [
        program.name,
        program.ticker,
        program.catalog.platform,
        program.catalog.assetClass,
        program.catalog.distributionTypes.join(' + '),
        program.catalog.jurisdiction,
        identity.issuer,
        identity.currency,
        identity.address,
        coverageLabel(program),
        activity ? `${activity.action} (${activity.source})` : '',
        activity?.href || '',
      ]);
    });
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `assetglobe-rwa-xyz-xrpl-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <section className="catalog-panel panel" aria-labelledby="catalog-title">
      <div className="catalog-header">
        <div>
          <span className="eyebrow">Attributed reference layer</span>
          <h2 id="catalog-title">RWA.xyz · XRP Ledger catalog</h2>
          <p>{status === 'ready' ? 'Every returned asset is searchable. Exact issuer/currency pairs participate in the live matcher; catalog inclusion is not upgraded to first-party verification.' : 'The catalog is fail-closed until an authorized public-display feed is available. Curated XRPL data elsewhere in the app remains live.'}</p>
        </div>
        <div className="catalog-source-meta">
          <strong>{status === 'ready' ? meta?.distinctAssets ?? programs.length : '—'} assets</strong>
          <span>{meta?.tokenRecords ?? '—'} token records</span>
          <span>{meta?.issuerAccounts ?? '—'} accounts</span>
          <time dateTime={meta?.sourceUpdatedAt || undefined}>Source {sourceTime(meta?.sourceUpdatedAt)}</time>
        </div>
      </div>

      <div className="catalog-controls">
        <label className="search-field catalog-search">
          <span className="sr-only">Search RWA.xyz catalog</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 21-4.4-4.4m2.4-5.1a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" /></svg>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search asset, ticker, platform, issuer or address" />
        </label>
        <label>
          <span className="sr-only">Distribution type</span>
          <select value={distribution} onChange={(event) => setDistribution(event.target.value)}>
            <option value="all">All distribution types</option>
            <option value="distributed">Onchain distributed</option>
            <option value="represented">Onchain represented</option>
          </select>
        </label>
        <label>
          <span className="sr-only">Platform</span>
          <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
            <option value="all">All platforms</option>
            {platforms.map((value) => <option value={value} key={value}>{value}</option>)}
          </select>
        </label>
        <button className="secondary-button" onClick={exportCsv} disabled={!filtered.length}>Export CSV</button>
      </div>

      {status === 'error' && (
        <div className="inline-error" role="status">
          <span>Catalog unavailable: {error}</span>
          {errorCode !== 'RWA_CATALOG_NOT_CONFIGURED' && <button onClick={retry}>Retry</button>}
        </div>
      )}

      <div className="catalog-results-meta"><strong>{filtered.length}</strong> matching assets</div>
      <div className="catalog-table-wrap">
        <table className="catalog-table">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Platform / class</th>
              <th>Distribution</th>
              <th>Ledger identity</th>
              <th>Coverage</th>
              <th>Latest activity</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, visibleCount).map((program) => {
              const identities = program.catalog.identities || [];
              const firstIdentity = identities[0];
              const activity = activityByProgram.get(program.id) || activityByProgram.get(program.catalog.coveredBy);
              return (
                <tr key={program.id}>
                  <td data-label="Asset">
                    <button className="table-asset-button" onClick={() => onSelect(program.id)}>
                      <strong>{program.ticker}</strong>
                      <span>{program.name}</span>
                    </button>
                  </td>
                  <td data-label="Platform / class"><strong>{program.catalog.platform}</strong><span>{program.catalog.assetClass}</span></td>
                  <td data-label="Distribution"><strong>{program.catalog.distributionTypes.map((value) => value.replace('Onchain ', '')).join(' + ')}</strong><span>{program.catalog.jurisdiction || 'Jurisdiction undisclosed'}</span></td>
                  <td data-label="Ledger identity"><code title={firstIdentity?.address || ''}>{firstIdentity?.address ? shortAddress(firstIdentity.address, 12, 9) : 'Not published'}</code><span>{identities.length > 1 ? `${identities.length} network records` : firstIdentity?.standard || 'Account reference'}</span></td>
                  <td data-label="Coverage"><span className={`coverage-badge ${program.catalog.coveredBy ? 'coverage-curated' : program.catalog.exactIdentityCount ? 'coverage-tracked' : 'coverage-reference'}`}>{coverageLabel(program)}</span><span>{program.catalog.coveredByName || (program.catalog.liveTrackable ? 'Eligible for live matching' : 'Identity needs review')}</span></td>
                  <td data-label="Latest activity">
                    {activity ? (
                      <a className="activity-link" href={activity.href} target="_blank" rel="noreferrer">
                        <strong>{activity.action} ↗</strong>
                        <span>{activity.source === 'live' ? 'LIVE' : 'HISTORY'} · {formatLedgerTime(activity.date)}</span>
                      </a>
                    ) : <><strong>None this session</strong><span>Listener stays active</span></>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {status === 'loading' && !programs.length && <div className="table-loading"><span />Loading the authorized catalog feed…</div>}
        {status === 'ready' && !filtered.length && <div className="empty-state">No catalog assets match these filters.</div>}
      </div>
      {visibleCount < filtered.length && (
        <button className="load-more" onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}>
          Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more <span>{visibleCount} of {filtered.length}</span>
        </button>
      )}
      <div className="catalog-attribution">
        <span>Authorized reference feed · no RWA.xyz valuation metrics reproduced</span>
        <a href={meta?.sourceUrl || 'https://app.rwa.xyz/networks/xrp-ledger'} target="_blank" rel="noreferrer">View source ↗</a>
      </div>
    </section>
  );
}
